import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import FullPageLoader from '../components/FullPageLoader';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  const loadProfile = useCallback(async (userId) => {
    if (!userId) return null
    setProfileLoading(true)
    try {
      const { data, error } = await supabase?.from('profiles')?.select('*, organizations(id, razon_social, nit, status)')?.eq('id', userId)?.single()

      if (!error && data) {
        setUserProfile(data)
        return data
      }
      return null
    } catch (_err) {
      return null
    } finally {
      setProfileLoading(false)
    }
  }, [])

  const clearProfile = useCallback(() => {
    setUserProfile(null)
    setProfileLoading(false)
  }, [])

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) {
        loadProfile(session?.user?.id)
      }
    })

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null)
        setLoading(false)
        if (event === 'SIGNED_IN' && session?.user) {
          // Profile already loaded by signIn — skip duplicate load
        } else if (session?.user) {
          loadProfile(session?.user?.id)
        } else {
          clearProfile()
        }
      }
    )

    return () => subscription?.unsubscribe()
  }, [loadProfile, clearProfile])

  const signIn = async (email, password) => {
    try {
      // Step 1: Authenticate
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })

      if (error) {
        return { data: null, error: { message: 'Correo o contraseña incorrectos' } }
      }

      if (!data?.user) {
        return { data: null, error: { message: 'Correo o contraseña incorrectos' } }
      }

      // Step 2: Load profile
      const { data: profileData, error: profileError } = await supabase?.from('profiles')?.select('*, organizations(id, razon_social, nit, status)')?.eq('id', data?.user?.id)?.single()

      // Step 3: Handle missing profile
      if (profileError?.code === 'PGRST116' || !profileData) {
        if (email === 'juanfergl@gmail.com') {
          // Auto-upsert SUPER_ADMIN for the admin user
          const { data: upserted, error: upsertError } = await supabase?.from('profiles')?.upsert(
              { id: data?.user?.id, email, full_name: 'Administrador', role: 'SUPER_ADMIN', status: 'ACTIVE' },
              { onConflict: 'id' }
            )?.select('*, organizations(id, razon_social, nit, status)')?.single()

          if (!upsertError && upserted) {
            setUserProfile(upserted)
            return { data: { ...data, profile: upserted }, error: null }
          }
        }
        // No profile for other users
        await supabase?.auth?.signOut()
        setUser(null)
        clearProfile()
        return { data: null, error: { message: 'Cuenta no encontrada, contacta al administrador', isStatusBlock: true } }
      }

      // Step 4: Check account status
      const status = profileData?.status
      if (status === 'PENDING') {
        await supabase?.auth?.signOut()
        setUser(null)
        clearProfile()
        return { data: null, error: { message: 'Tu cuenta está pendiente de activación', isStatusBlock: true } }
      }
      if (status === 'SUSPENDED') {
        await supabase?.auth?.signOut()
        setUser(null)
        clearProfile()
        return { data: null, error: { message: 'Tu cuenta ha sido suspendida', isStatusBlock: true } }
      }
      if (status === 'REJECTED') {
        await supabase?.auth?.signOut()
        setUser(null)
        clearProfile()
        return { data: null, error: { message: 'Tu solicitud no fue aprobada', isStatusBlock: true } }
      }

      // Step 5: ACTIVE — set profile and return
      setUserProfile(profileData)
      return { data: { ...data, profile: profileData }, error: null }

    } catch (_err) {
      return { data: null, error: { message: 'No se pudo conectar al servidor', isNetworkError: true } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        clearProfile()
      }
      return { error }
    } catch (_err) {
      return { error: { message: 'Error de red. Intenta de nuevo.' } }
    }
  }

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/login`,
      })
      return { error }
    } catch (_err) {
      return { error: { message: 'Error de red. Intenta de nuevo.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No hay usuario autenticado' } }
    try {
      const { data, error } = await supabase?.from('profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (_err) {
      return { error: { message: 'Error de red. Intenta de nuevo.' } }
    }
  }

  const getRedirectPath = (profile) => {
    const role = profile?.role
    if (role === 'SUPER_ADMIN' || role === 'ADMIN') return '/administrative-dashboard'
    if (role === 'OPERATOR') return '/update-management'
    if (role === 'CUSTOMER_USER') return '/customer-user-dashboard'
    return '/administrative-dashboard'
  }

  const getRoleLabel = (role) => {
    const labels = {
      SUPER_ADMIN: 'Super Administrador',
      ADMIN: 'Administrador',
      OPERATOR: 'Operador',
      CUSTOMER_USER: 'Usuario',
    }
    return labels?.[role] || role || 'Usuario';
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    loadProfile,
    getRedirectPath,
    getRoleLabel,
    isAuthenticated: !!user,
  }

  if (loading) {
    return <FullPageLoader />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
